import React, { FC } from 'react';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from 'react-i18next';
import { formatDuration, addSeconds, differenceInDays } from 'date-fns';
import { AccountTypes } from '../../../models/Common';
import { TextButton, TextButtonClassNames } from '../../commons/buttons/TextButton';
import { OnlineTime } from '../../../models/Sessions';
import { useAppSelector } from '../../../hooks/redux';
import { useDateFsnLocale } from '../../../hooks/useDateFsnLocale';
import { BasicTable } from '../../commons/customTables/BasicTable';
import { CustomTableClassNames } from '../../../models/CustomTable';
import { ArchiveSessions } from './ArchiveSessions';
import { OnlineSession } from './OnlineSession';
import { internetApi } from '../../../services/apiServices/user/internet';

interface SessionsProps {
  accountId: string | number;
  accountType: AccountTypes | string;
  isUnion: boolean;
}

export interface SessionRowProps {
  id: string | number;
  start: string;
  onlineTime: string;
  ipv4: string;
  ipv4Sent: string;
  ipv4Received: string;
  ipv6: string;
  ipv6Sent: string;
  ipv6Received: string;
  end?: string;
}

export const Sessions: FC<SessionsProps> = ({ accountId, accountType, isUnion }) => {
  const { t } = useTranslation();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { data: currentSession } = internetApi.useGetCurrentSessionQuery(
    { accountId },
    { skip: accountType !== AccountTypes.Internet && !isUnion },
  );
  const onlineSession = currentSession?.sessions[0];
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const [open, setOpen] = React.useState(false);

  const formatOnlineTime = (time: OnlineTime) => {
    const now = new Date();
    const future = addSeconds(now, time.ts);
    const days = differenceInDays(future, now);
    const options = {
      locale: fnsLocale,
      zero: true,
    };
    let hours = String(time?.hours);
    hours = hours?.padStart(2, '0');
    let minutes = String(time?.minutes);
    minutes = minutes?.padStart(2, '0');
    let seconds = String(time?.seconds);
    seconds = seconds?.padStart(2, '0');
    const entireTime = `${hours}:${minutes}:${seconds}`;
    return `${formatDuration({ days }, options)} ${entireTime}`;
  };

  const includedTableHeader = [
    t('serviceManagement.externalIp'),
    t('serviceManagement.sent'),
    t('serviceManagement.downloaded'),
  ];

  const createRow = ({
    id,
    start,
    onlineTime,
    ipv4,
    ipv4Sent,
    ipv4Received,
    ipv6,
    ipv6Sent,
    ipv6Received,
    end,
  }: SessionRowProps) => {
    return {
      id,
      bodyCells: end ? [start, end, onlineTime] : [start, onlineTime],
      isCollapsibleButton: true,
      history: (
        <BasicTable
          className={CustomTableClassNames.AlignStart}
          tableHead={includedTableHeader}
          tableBodyCells={[
            { id: `1${id}`, bodyCells: [`ipv4: ${ipv4}`, ipv4Sent, ipv4Received] },
            { id: `2${id}`, bodyCells: [`ipv6: ${ipv6}`, ipv6Sent, ipv6Received] },
          ]}
        />
      ),
    };
  };

  return (
    <div className="sessions">
      <h3 className="title">{t('serviceManagement.sessions')}</h3>
      <div className="sessions__tables">
        <div className="sessions__tables__current-session">
          <article className={`sessions__tables__current-session__${onlineSession ? 'online' : 'offline'}`}>
            {t('serviceManagement.onlineSession')}
          </article>
          <OnlineSession formatOnlineTime={formatOnlineTime} createRow={createRow} onlineSession={onlineSession} />
        </div>
        <div className="sessions__tables__archive-sessions">
          <div className="sessions__tables__archive-sessions__header">
            <article className="sessions__tables__archive-sessions__header__title">
              {t('serviceManagement.sessionArchive')}
            </article>
            <TextButton
              label={open ? t('buttons.hideArchive') : t('buttons.showArchive')}
              handleClick={() => setOpen(!open)}
              className={!open ? TextButtonClassNames.PrimaryActive : TextButtonClassNames.Red}
            />
          </div>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <ArchiveSessions
              accountId={accountId}
              accountType={accountType}
              isUnion={isUnion}
              formatOnlineTime={formatOnlineTime}
              createRow={createRow}
            />
          </Collapse>
        </div>
      </div>
    </div>
  );
};
